import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getRoot} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings/RoleContext';
import {useCallback, useEffect, useRef} from 'react';

import {EnhancedTableCellNode} from './EnhancedTableCellNode';
import {$createTableCellDecoratorNode, $isTableCellDecoratorNode} from './TableCellDecoratorNode';
import {registerTableCellNode} from './TableCellNodeUtils';

export const TableCellPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();
  const isManagingNodes = useRef(false);

  const manageTableCellNodes = useCallback(() => {
    if (isManagingNodes.current) {
      return; // Prevent recursive calls
    }

    isManagingNodes.current = true;
    editor.update(() => {
      const root = $getRoot();

      // Find all enhanced table cell nodes
      const findTableCellNodes = (node: any) => {
        if (node instanceof EnhancedTableCellNode) {
          // Only process header cells (th elements)
          if (node.getHeaderStyles() === 0) {
            return; // Skip non-header cells
          }

          const cellId = node.getCellId();
          const guidance = node.getGuidance();

          // Register table cell node
          registerTableCellNode(cellId, node.getKey());

          // Check if there's already a decorator node for this cell
          const children = node.getChildren();
          const existingDecoratorNodes = children.filter(
            (child) => $isTableCellDecoratorNode(child) && child.getCellId() === cellId
          );

          // Remove any duplicate decorator nodes - keep only the first one
          if (existingDecoratorNodes.length > 1) {
            existingDecoratorNodes.slice(1).forEach((duplicateNode) => {
              duplicateNode.remove();
            });
          }

          const existingDecoratorNode = existingDecoratorNodes[0];

          // In template mode, always show decorator node (for gear icon)
          if (isTemplateMode) {
            if (!existingDecoratorNode) {
              const decoratorNode = $createTableCellDecoratorNode(cellId, guidance || '');
              node.append(decoratorNode);
            } else {
              // Update existing decorator node
              (existingDecoratorNode as any).setGuidance(guidance || '');
            }
          } else {
            // In document mode, only show decorator node if there's guidance
            if (guidance && guidance.trim()) {
              if (existingDecoratorNode) {
                // Update existing decorator node
                (existingDecoratorNode as any).setGuidance(guidance);
              } else {
                // Create new decorator node
                const decoratorNode = $createTableCellDecoratorNode(cellId, guidance);
                node.append(decoratorNode);
              }
            } else {
              // No guidance text - remove decorator node if it exists
              if (existingDecoratorNode) {
                existingDecoratorNode.remove();
              }
            }
          }
        }

        // Recursively check children
        if (node.getChildren) {
          node.getChildren().forEach(findTableCellNodes);
        }
      };

      // Start from root and search recursively
      findTableCellNodes(root);
    });

    // Reset the flag after a short delay to allow for any pending updates
    setTimeout(() => {
      isManagingNodes.current = false;
    }, 0);
  }, [editor, isTemplateMode]);

  useEffect(() => {
    // Initial setup
    manageTableCellNodes();

    // Handle when table cells are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedTableCellNode, () => {
      manageTableCellNodes();
    });

    return () => {
      removeNodeTransform();
    };
  }, [editor, manageTableCellNodes]);

  return null;
};
